/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, type ReactElement, useState, useCallback } from 'react';
import { Await } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Link } from '@/components/link';
import { useKlaviyoSubscribe } from '@/hooks/use-klaviyo-subscribe';
// RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): TrueVault visible compliance links.
import {
    CaliforniaPrivacyNoticeLink,
    FinancialIncentiveNoticeLink,
    PrivacyChoicesOptOutLink,
} from '@/components/truevault-notices';
import { useTrueVaultPrivacyLinks } from '@/components/truevault-notices/use-truevault-privacy-links';
import logo from '/images/logo.svg';
// RGBARRY-CUSTOMIZATION: consume merchant-authored footer columns while retaining the current links as fallbacks.
import type {
    FooterColumnConfig,
    FooterColumnId,
    FooterLinkConfig,
    FooterNavigationOverrides,
} from '@/components/footer-navigation/model';

/**
 * RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): Privacy Policy link that repoints to the
 * TrueVault Privacy Center's hosted policy when TrueVault is configured (per TrueVault Website
 * Implementation step 1 — "update all Privacy Policy links"), and falls back to the internal /privacy
 * page otherwise. External links must be a plain <a> (the site-context Link would prefix the path).
 */
function PrivacyPolicyLink({ className }: { className?: string }): ReactElement {
    const links = useTrueVaultPrivacyLinks();
    if (links) {
        return (
            <a href={links.privacyPolicy} rel="noopener noreferrer" className={className}>
                Privacy Policy
            </a>
        );
    }
    return (
        // RGBARRY-CUSTOMIZATION: internal fallback is the /privacy-policy.html content asset
        // (200), not the template's bare /privacy placeholder (404), for the TrueVault-off case.
        // prefetch=none: footer links sit in/near the viewport; RR intent prefetch showed as pointerover TBT on PDP.
        <Link to="/privacy-policy.html" prefetch="none" className={className}>
            Privacy Policy
        </Link>
    );
}

/**
 * RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): footer opt-out area. When TrueVault is
 * configured, render TrueVault's "Your Privacy Choices" opt-out + California Privacy Notice (both
 * hidden until Polaris reveals them per state); otherwise keep an always-visible consent-manager
 * entry point so there is never a window with no privacy control at all.
 */
function FooterPrivacyChoices({ className }: { className?: string }): ReactElement {
    const links = useTrueVaultPrivacyLinks();
    if (links) {
        return (
            <>
                <PrivacyChoicesOptOutLink className={className} />
                <CaliforniaPrivacyNoticeLink className={className} />
            </>
        );
    }
    return (
        <a href="https://www.baggallini.com/?polaris.openConsentManager=true" className={className}>
            Cookie Preferences
        </a>
    );
}

// RGBARRY-CUSTOMIZATION: mirror the Klaviyo subscribe validation (server action + custom API)
// so the footer strips fail fast without a round trip.
const EMAIL_REGEX = /^[\w.%+-]+@[\w.-]+\.[\w]{2,6}$/;

/** Normalize free-form US phone input to the `ddd-ddd-dddd` format the subscribe action expects. */
function normalizeUsPhone(raw: string): string | null {
    const digits = raw.replace(/\D/g, '');
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length !== 10) return null;
    return `${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
}

function EmailSignupStrip(): ReactElement {
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    // RGBARRY-CUSTOMIZATION: real Klaviyo email subscription via the server-action proxy.
    const { subscribe, isSubmitting } = useKlaviyoSubscribe(
        () => {
            setError(null);
            setSubmitted(true);
        },
        () => setError('Something went wrong. Please try again.')
    );

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            setError(null);
            const value = email.trim();
            if (!EMAIL_REGEX.test(value)) {
                setError('Please enter a valid email address.');
                return;
            }
            subscribe({ source: 'email', email: value });
        },
        [email, subscribe]
    );

    return (
        <div className="border-b border-[#d4cec7]">
            {/* RGBARRY-CUSTOMIZATION: grid-cols-1 (minmax(0,1fr)) so the signup input cannot widen the mobile track. */}
            <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left */}
                <div>
                    <h2 className="font-[family-name:var(--font-serif)] text-3xl lg:text-4xl font-light italic text-[#1a1a1a] mb-2">
                        Be the first to know.
                    </h2>
                    <p className="text-sm text-[#1a1a1a]/70 leading-relaxed">
                        Subscribe via email and be the first to know about new arrivals and exclusive offers. Plus,
                        enjoy <span className="text-[#1a1a1a] font-medium">15% off</span> your first purchase.
                    </p>
                </div>

                {/* Right */}
                <div>
                    {submitted ? (
                        <div role="status" className="flex items-center gap-2 text-[#1a1a1a] text-sm">
                            {/* RGBARRY-CUSTOMIZATION: announce asynchronous subscription confirmation without moving focus. */}
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Thanks! Check your inbox for your discount code.
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex gap-0">
                            {/* RGBARRY-CUSTOMIZATION: expose the input purpose to browsers and assistive technology;
                                min-w-0 lets the input shrink so it cannot overflow a 390px viewport (iPhone 14), and
                                16px on mobile stops iOS focus auto-zoom. */}
                            <input
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                aria-invalid={!!error}
                                className="flex-1 min-w-0 bg-[#e4ddd5] text-[#1a1a1a] placeholder:text-[#1a1a1a]/70 border border-[#c4bdb5] border-r-0 px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors"
                                required
                            />
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#1a1a1a] hover:bg-[#333] text-[#ece6db] text-xs tracking-[0.12em] uppercase font-medium px-6 py-3 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                Subscribe
                            </button>
                        </form>
                    )}
                    {/* RGBARRY-CUSTOMIZATION: subscribe validation / failure message. */}
                    {error && (
                        <p role="alert" className="text-[11px] text-[#b3261e] mt-2">
                            {error}
                        </p>
                    )}
                    <p className="text-[11px] text-[#1a1a1a]/70 mt-2 leading-relaxed">
                        By subscribing you agree to our{' '}
                        <PrivacyPolicyLink className="underline hover:text-[#1a1a1a] transition-colors" />. Unsubscribe
                        anytime.{' '}
                        {/* RGBARRY-CUSTOMIZATION (DRAFT, W-23789028): TrueVault point-of-collection notices.
                            Email signup offers a discount, so it's a financial-incentive opt-in. Both are
                            hidden until Polaris reveals them per the visitor's state. */}
                        <FinancialIncentiveNoticeLink className="underline hover:text-[#1a1a1a] transition-colors" />{' '}
                        <CaliforniaPrivacyNoticeLink className="underline hover:text-[#1a1a1a] transition-colors" />
                    </p>
                </div>
            </div>
        </div>
    );
}

function SmsSignupStrip(): ReactElement {
    const [phone, setPhone] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = useState(false);

    // RGBARRY-CUSTOMIZATION: real Klaviyo SMS subscription via the server-action proxy.
    const { subscribe, isSubmitting } = useKlaviyoSubscribe(
        () => {
            setError(null);
            setSubmitted(true);
        },
        () => setError('Something went wrong. Please try again.')
    );

    const handleSubmit = useCallback(
        (e: React.FormEvent) => {
            e.preventDefault();
            setError(null);
            const formatted = normalizeUsPhone(phone);
            if (!formatted) {
                setError('Enter a 10-digit US phone number.');
                return;
            }
            subscribe({ source: 'sms', phone: formatted });
        },
        [phone, subscribe]
    );

    return (
        <div className="border-b border-[#d4cec7]">
            {/* RGBARRY-CUSTOMIZATION: grid-cols-1 (minmax(0,1fr)) so the signup input cannot widen the mobile track. */}
            <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-12 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                {/* Left — form (inverted vs email strip) */}
                <div>
                    {submitted ? (
                        <div role="status" className="flex items-center gap-2 text-[#1a1a1a] text-sm">
                            {/* RGBARRY-CUSTOMIZATION: announce asynchronous subscription confirmation without moving focus. */}
                            <svg
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12" />
                            </svg>
                            You&apos;re signed up! Watch for your first text.
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="flex gap-0">
                            {/* RGBARRY-CUSTOMIZATION: expose the input purpose to browsers and assistive technology;
                                min-w-0 lets the input shrink so it cannot overflow a 390px viewport (iPhone 14), and
                                16px on mobile stops iOS focus auto-zoom. */}
                            <input
                                type="tel"
                                autoComplete="tel"
                                inputMode="tel"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="+(1) 000-000-0000"
                                aria-invalid={!!error}
                                className="flex-1 min-w-0 bg-[#e4ddd5] text-[#1a1a1a] placeholder:text-[#1a1a1a]/70 border border-[#c4bdb5] border-r-0 px-4 py-3 text-base sm:text-sm focus:outline-none focus:border-[#1a1a1a] transition-colors"
                                required
                            />
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="bg-[#1a1a1a] hover:bg-[#333] text-[#ece6db] text-xs tracking-[0.12em] uppercase font-medium px-6 py-3 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                                Sign Up
                            </button>
                        </form>
                    )}
                    {/* RGBARRY-CUSTOMIZATION: subscribe validation / failure message. */}
                    {error && (
                        <p role="alert" className="text-[11px] text-[#b3261e] mt-2">
                            {error}
                        </p>
                    )}
                    <p className="text-[11px] text-[#1a1a1a]/70 mt-2 leading-relaxed">
                        By submitting this form, you agree to receive recurring marketing text messages, which may be
                        automated, to the mobile number provided at opt-in. Consent is not a condition of purchase. Msg
                        frequency may vary. Msg &amp; data rates may apply. Reply HELP for help and STOP to cancel. See{' '}
                        {/* RGBARRY-CUSTOMIZATION: prod content-asset path (/terms-of-use.html);
                            the template's bare /terms placeholder has no route/content and 404s. */}
                        <Link
                            to="/terms-of-use.html"
                            prefetch="none"
                            className="underline hover:text-[#1a1a1a] transition-colors">
                            Terms and Conditions
                        </Link>{' '}
                        &amp; <PrivacyPolicyLink className="underline hover:text-[#1a1a1a] transition-colors" />.{' '}
                        {/* RGBARRY-CUSTOMIZATION (DRAFT, W-23789028): TrueVault point-of-collection notice —
                            hidden until Polaris reveals per state. */}
                        <CaliforniaPrivacyNoticeLink className="underline hover:text-[#1a1a1a] transition-colors" />
                    </p>
                </div>

                {/* Right — text */}
                <div className="lg:order-last order-first">
                    <h2 className="font-[family-name:var(--font-serif)] text-3xl lg:text-4xl font-light italic text-[#1a1a1a] mb-2">
                        Always in the Know.
                    </h2>
                    <p className="text-sm text-[#1a1a1a]/70 leading-relaxed">
                        Sign up to receive text alerts and unlock{' '}
                        <span className="text-[#1a1a1a] font-medium">15% off</span> your first purchase — because your
                        next adventure deserves a stylish start.
                    </p>
                </div>
            </div>
        </div>
    );
}

// RGBARRY-CUSTOMIZATION: one link renderer keeps authored and fallback link behavior identical on desktop/mobile.
function FooterNavigationLink({ link, className }: { link: FooterLink; className: string }): ReactElement {
    const isAnchor = link.external === true || /^(?:https?:)?\/\//i.test(link.href) || link.href.startsWith('?');
    const openInNewTab = link.newTab ?? link.external === true;
    if (isAnchor) {
        return (
            <a
                href={link.href}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : undefined}
                className={className}>
                {link.label}
            </a>
        );
    }
    return (
        <Link to={link.href} prefetch="none" className={className}>
            {link.label}
        </Link>
    );
}

function MobileAccordion({ title, links }: { title: string; links: FooterLink[] }): ReactElement {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-[#d4cec7]">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between py-4 text-left cursor-pointer">
                <span className="text-xs tracking-[0.15em] uppercase font-medium text-[#1a1a1a]">{title}</span>
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#1a1a1a"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-200 opacity-60 ${open ? 'rotate-180' : ''}`}>
                    <polyline points="6 9 12 15 18 9" />
                </svg>
            </button>
            {open && (
                <div className="pb-4 space-y-3">
                    {links.map((link) => (
                        <FooterNavigationLink
                            key={`${link.label}-${link.href}`}
                            link={link}
                            className="block text-sm text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors duration-150"
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// RGBARRY-CUSTOMIZATION: footer links that used to point at the absolute www.baggallini.com
// domain are now same-origin relative paths so they open in the current tab on whatever origin
// the storefront is served from (dev/staging/prod), keeping the existing page path. Genuinely
// off-site destinations (Careers → recruiting.paylocity.com, social) stay absolute + external.
type FooterLink = FooterLinkConfig & { external?: boolean };

const ABOUT_LINKS: FooterLink[] = [
    // RGBARRY-CUSTOMIZATION: the rebuilt Our Story page is served at /our-story.html (see
    // src/routes/_app.our-story[.html].tsx); /ourstory.html is a stale content-asset slug with
    // no redirect to the current page, so it must not be linked here.
    { label: 'Our Story', href: '/our-story.html' },
    { label: 'Our Materials', href: '/materials.html' },
    {
        label: 'Careers',
        href: 'https://recruiting.paylocity.com/recruiting/jobs/All/1d3c15b6-0519-49a1-8dc6-291e3fb07778/R-G-BARRY-CORPORATION-SUBSID',
        external: true,
    },
    { label: 'Become a Retailer', href: '/become-a-retailer.html' },
    { label: 'Become an Affiliate', href: '/affiliates.html' },
    { label: 'Our Blog', href: '/baggallini-blog.html' },
    // RGBARRY-CUSTOMIZATION (W-23789028 consent): privacy is TrueVault-authoritative — Privacy
    // Policy is rendered by the TrueVault-aware PrivacyPolicyLink, so we do not list a static
    // Privacy Policy link here. Terms of Use is not CMP-owned, so it stays.
    { label: 'Terms of Use', href: '/terms-of-use.html' },
    // RGBARRY-CUSTOMIZATION: always-visible entry point for reopening the Polaris consent manager.
    // TrueVault documents the plain relative query link as the non-regional form; do not add the
    // `truevault-polaris-open-consent-manager-link` class, which Polaris shows/hides by region.
    {
        label: 'Cookie Preferences',
        href: '?polaris.openConsentManager=true',
        external: true,
        newTab: false,
    },
];

const CARE_LINKS: FooterLink[] = [
    { label: 'Contact Us', href: '/contact-us' },
    { label: 'Frequently Asked Questions', href: '/faq' },
    { label: 'Returns & Exchanges', href: '/return-policy.html' },
    { label: 'Warranty', href: '/warranty.html' },
    { label: 'Shipping Methods and Costs', href: '/shipping-methods-and-costs.html' },
    // RGBARRY-CUSTOMIZATION: expose Baggallini's accessibility commitment from Customer Care.
    { label: 'Website Accessibility', href: '/website-accessibility' },
];

const ACCOUNT_LINKS: FooterLink[] = [
    { label: 'My Account', href: '/account' },
    // RGBARRY-CUSTOMIZATION: point the guest "Order Status" entry at the Track Order page
    // (guest order lookup); the prior '/order-status' target had no route/content page.
    { label: 'Order Status', href: '/track-order' },
];

const DEFAULT_FOOTER_COLUMNS: FooterColumnConfig[] = [
    { id: 'about', title: 'About Us', links: ABOUT_LINKS },
    { id: 'customerCare', title: 'Customer Care', links: CARE_LINKS },
    { id: 'account', title: 'Account', links: ACCOUNT_LINKS },
];

// RGBARRY-CUSTOMIZATION: merge each valid authored region independently so incomplete PD work cannot blank the footer.
// oxlint-disable-next-line react-refresh/only-export-components
export function resolveFooterColumns(overrides?: FooterNavigationOverrides | null): FooterColumnConfig[] {
    return DEFAULT_FOOTER_COLUMNS.map((fallback) => overrides?.[fallback.id as FooterColumnId] ?? fallback);
}

function FooterNavigationColumns({ columns }: { columns: FooterColumnConfig[] }): ReactElement {
    return (
        <>
            {columns.map((column) => (
                <div key={column.id} className="hidden lg:block">
                    <h3 className="text-xs tracking-[0.15em] uppercase font-medium text-[#1a1a1a] mb-5">
                        {column.title}
                    </h3>
                    <ul className="space-y-3">
                        {column.links.map((link) => (
                            <li key={`${link.label}-${link.href}`}>
                                <FooterNavigationLink
                                    link={link}
                                    className="text-sm text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors duration-150 leading-snug"
                                />
                            </li>
                        ))}
                    </ul>
                </div>
            ))}

            <div className="lg:hidden col-span-full">
                {columns.map((column) => (
                    <MobileAccordion key={column.id} title={column.title} links={column.links} />
                ))}
            </div>
        </>
    );
}

type FooterNavigationSource = FooterNavigationOverrides | Promise<FooterNavigationOverrides | null> | null | undefined;

function FooterNavigationSection({ navigation }: { navigation: FooterNavigationSource }): ReactElement {
    const fallback = <FooterNavigationColumns columns={resolveFooterColumns()} />;
    if (navigation instanceof Promise) {
        return (
            <Suspense fallback={fallback}>
                <Await resolve={navigation} errorElement={fallback}>
                    {(overrides: FooterNavigationOverrides | null) => (
                        <FooterNavigationColumns columns={resolveFooterColumns(overrides)} />
                    )}
                </Await>
            </Suspense>
        );
    }
    return <FooterNavigationColumns columns={resolveFooterColumns(navigation)} />;
}
// END RGBARRY-CUSTOMIZATION

export default function MainFooter({ navigation }: { navigation?: FooterNavigationSource }): ReactElement {
    const { t } = useTranslation('footer');

    return (
        <footer className="mt-auto bg-[#ece6db] text-[#1a1a1a]">
            <EmailSignupStrip />
            <SmsSignupStrip />

            {/* Main grid */}
            <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-12">
                <div className="grid lg:grid-cols-[2fr_1fr_1fr_1fr] gap-12">
                    {/* Brand column */}
                    <div className="lg:pr-8">
                        {/* Logo */}
                        <Link to="/" prefetch="none" className="inline-block mb-6">
                            <img src={logo} alt={t('logoAlt')} className="h-6 w-auto opacity-90" />
                        </Link>

                        {/* Description */}
                        <p className="text-sm text-[#1a1a1a]/70 leading-relaxed mb-6 max-w-[280px]">
                            Baggallini creates organized bags that simplify life — thoughtfully designed for the way you
                            move through the world.
                        </p>

                        {/* Social icons */}
                        <div>
                            <p className="text-[11px] tracking-[0.15em] uppercase text-[#1a1a1a]/70 mb-3">Follow Us</p>
                            <div className="flex items-center gap-3">
                                {/* Instagram */}
                                <a
                                    href="https://www.instagram.com/baggallini/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Instagram"
                                    className="flex items-center justify-center w-9 h-9 border border-[#c4bdb5] text-[#1a1a1a]/70 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors duration-200 rounded-sm">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                                    </svg>
                                </a>
                                {/* Facebook */}
                                <a
                                    href="https://www.facebook.com/baggallini/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="Facebook"
                                    className="flex items-center justify-center w-9 h-9 border border-[#c4bdb5] text-[#1a1a1a]/70 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors duration-200 rounded-sm">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                                    </svg>
                                </a>
                                {/* YouTube */}
                                <a
                                    href="https://www.youtube.com/@baggallini"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label="YouTube"
                                    className="flex items-center justify-center w-9 h-9 border border-[#c4bdb5] text-[#1a1a1a]/70 hover:border-[#1a1a1a] hover:text-[#1a1a1a] transition-colors duration-200 rounded-sm">
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round">
                                        <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 12a29 29 0 0 0 .46 5.58A2.78 2.78 0 0 0 3.41 19.54C5.12 20 12 20 12 20s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.96A29 29 0 0 0 23 12a29 29 0 0 0-.46-5.58z" />
                                        <polygon points="9.75 15.02 15.5 12 9.75 8.98 9.75 15.02" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* RGBARRY-CUSTOMIZATION: Page Designer controls headings, link text, URLs, and ordering. */}
                    <FooterNavigationSection navigation={navigation} />
                    {/* END RGBARRY-CUSTOMIZATION */}
                </div>
            </div>

            {/* Bottom bar */}
            <div className="border-t border-[#d4cec7]">
                <div className="max-w-[1280px] mx-auto px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="text-[12px] text-[#1a1a1a]/70">
                        © {new Date().getFullYear()} Baggallini. All rights reserved.
                    </p>
                    {/* RGBARRY-CUSTOMIZATION: wrap the expanded legal-link set within narrow mobile viewports. */}
                    <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2">
                        <PrivacyPolicyLink className="text-[12px] text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors duration-150 whitespace-nowrap" />
                        {/* RGBARRY-CUSTOMIZATION: prod content-asset path (/terms-of-use.html);
                            the template's bare /terms placeholder has no route/content and 404s. */}
                        <Link
                            to="/terms-of-use.html"
                            prefetch="none"
                            className="text-[12px] text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors duration-150 whitespace-nowrap">
                            Terms of Use
                        </Link>
                        {/* RGBARRY-CUSTOMIZATION (DRAFT, W-23789028): TrueVault opt-out / CA notice when
                            configured, else the consent-manager trigger. */}
                        <FooterPrivacyChoices className="text-[12px] text-[#1a1a1a]/70 hover:text-[#1a1a1a] transition-colors duration-150 whitespace-nowrap" />
                    </div>
                </div>
            </div>
        </footer>
    );
}
